import React from 'react';
import { Trophy, Users, Tv, Star, MapPin, Calendar } from 'lucide-react';
import { Button } from './ui/button';
import { useNavigate } from 'react-router-dom';

const WorldCupPromoSection = () => {
  const navigate = useNavigate();

  return (
    <section className="relative py-20 overflow-hidden bg-gradient-to-b from-black via-slate-900 to-black">
      {/* Background effects */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_30%,rgba(223,255,0,0.15),transparent_60%)]" />
        <div className="absolute top-0 left-0 w-full h-full opacity-10">
          <div className="absolute top-20 left-10 w-32 h-32 border-2 border-[#DFFF00] rounded-full" />
          <div className="absolute bottom-20 right-10 w-24 h-24 border-2 border-[#DFFF00] rounded-full" />
          <div className="absolute top-1/2 left-1/4 w-16 h-16 border-2 border-[#DFFF00] rounded-full" />
        </div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Badge */}
        <div className="flex justify-center mb-8">
          <div className="inline-flex items-center gap-2 bg-[#DFFF00]/10 border border-[#DFFF00]/30 rounded-full px-5 py-2">
            <Trophy className="w-5 h-5 text-[#DFFF00]" />
            <span className="text-[#DFFF00] font-semibold text-sm tracking-wide">COUPE DU MONDE 2026</span>
          </div>
        </div>

        {/* Main Title */}
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
            Préparez-vous pour la{' '}
            <span className="text-[#DFFF00]">Coupe du Monde</span>
          </h2>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto leading-relaxed">
            L'événement sportif le plus regardé au monde arrive ! Offrez à vos clients une expérience immersive avec nos écrans LED géants.
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16">
          <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-6 border border-slate-700 text-center">
            <div className="text-4xl font-bold text-[#DFFF00] mb-2">5Mrd</div>
            <div className="text-slate-400 text-sm">Téléspectateurs attendus</div>
          </div>
          <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-6 border border-slate-700 text-center">
            <div className="text-4xl font-bold text-[#DFFF00] mb-2">64</div>
            <div className="text-slate-400 text-sm">Matchs à diffuser</div>
          </div>
          <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-6 border border-slate-700 text-center">
            <div className="text-4xl font-bold text-[#DFFF00] mb-2">30j</div>
            <div className="text-slate-400 text-sm">De compétition</div>
          </div>
          <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-6 border border-slate-700 text-center">
            <div className="text-4xl font-bold text-[#DFFF00] mb-2">48</div>
            <div className="text-slate-400 text-sm">Équipes participantes</div>
          </div>
        </div>

        {/* Benefits Grid */}
        <div className="grid md:grid-cols-3 gap-8 mb-16">
          <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-sm rounded-2xl p-8 border border-slate-700 hover:border-[#DFFF00]/50 transition-all duration-300">
            <div className="w-14 h-14 bg-[#DFFF00]/10 rounded-xl flex items-center justify-center mb-6">
              <Users className="w-7 h-7 text-[#DFFF00]" />
            </div>
            <h3 className="text-xl font-bold text-white mb-3">Fan Zones & Bars</h3>
            <p className="text-slate-400 leading-relaxed">
              Créez l'ambiance des grands stades dans votre établissement. Attirez les foules avec un écran géant visible de loin.
            </p>
          </div>

          <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-sm rounded-2xl p-8 border border-slate-700 hover:border-[#DFFF00]/50 transition-all duration-300">
            <div className="w-14 h-14 bg-[#DFFF00]/10 rounded-xl flex items-center justify-center mb-6">
              <Tv className="w-7 h-7 text-[#DFFF00]" />
            </div>
            <h3 className="text-xl font-bold text-white mb-3">Événements Corporate</h3>
            <p className="text-slate-400 leading-relaxed">
              Organisez des soirées foot pour vos clients ou collaborateurs. Un moment de partage inoubliable autour des matchs.
            </p>
          </div>

          <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-sm rounded-2xl p-8 border border-slate-700 hover:border-[#DFFF00]/50 transition-all duration-300">
            <div className="w-14 h-14 bg-[#DFFF00]/10 rounded-xl flex items-center justify-center mb-6">
              <MapPin className="w-7 h-7 text-[#DFFF00]" />
            </div>
            <h3 className="text-xl font-bold text-white mb-3">Collectivités & Mairies</h3>
            <p className="text-slate-400 leading-relaxed">
              Rassemblez vos citoyens sur la place publique. Un moment fédérateur pour toute la commune.
            </p>
          </div>
        </div>

        {/* CTA Section */}
        <div className="relative bg-gradient-to-r from-[#DFFF00]/20 via-[#DFFF00]/10 to-[#DFFF00]/20 rounded-3xl p-8 md:p-12 border border-[#DFFF00]/30">
          <div className="absolute -top-4 left-1/2 -translate-x-1/2">
            <div className="bg-[#DFFF00] text-black px-4 py-1 rounded-full text-sm font-bold flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              11 Juin - 19 Juillet 2026
            </div>
          </div>
          
          <div className="text-center pt-4">
            <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">
              Réservez dès maintenant votre écran LED
            </h3>
            <p className="text-slate-300 mb-8 max-w-2xl mx-auto">
              Les disponibilités seront limitées pendant la Coupe du Monde. 
              <strong className="text-[#DFFF00]"> Anticipez votre réservation</strong> pour garantir votre équipement.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={() => navigate('/reservation')}
                className="bg-[#DFFF00] hover:bg-[#c8e600] text-black font-semibold px-8 py-6 text-lg rounded-xl"
              >
                <Trophy className="w-5 h-5 mr-2" />
                Réserver pour la Coupe du Monde
              </Button>
              <Button 
                variant="outline"
                onClick={() => navigate('/contact')}
                className="border-slate-600 text-white hover:bg-slate-800 px-8 py-6 text-lg rounded-xl"
              >
                Demander un devis
              </Button>
            </div>
          </div>
        </div>

        {/* Trust indicators */}
        <div className="mt-12 flex flex-wrap justify-center gap-8 text-slate-500 text-sm">
          <div className="flex items-center gap-2">
            <Star className="w-4 h-4 text-[#DFFF00]" />
            <span>Installation incluse</span>
          </div>
          <div className="flex items-center gap-2">
            <Star className="w-4 h-4 text-[#DFFF00]" />
            <span>Support technique 7j/7</span>
          </div>
          <div className="flex items-center gap-2">
            <Star className="w-4 h-4 text-[#DFFF00]" />
            <span>Livraison France entière</span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WorldCupPromoSection;
